import { Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ASSISTANTS, NO_ASSISTANT, getAssistant, type AssistantGender } from "@/lib/assistants";

export interface AssistantChoice { gender: AssistantGender; id: string | null; name: string }

/** Avatar del asistente sobre círculo amarillo (mismo halo en todas partes). */
export function AssistantAvatar({ id, size = 40, className }: { id: string; size?: number; className?: string }) {
  const def = getAssistant(id);
  if (!def) return null;
  return (
    <span className={cn("relative inline-block shrink-0 overflow-hidden rounded-full border border-foreground/10 bg-primary/60", className)} style={{ width: size, height: size }}>
      <img src={def.img} alt={def.name} loading="lazy" className="absolute max-w-none" style={{ width: "120%", left: "-10%", top: "6%" }} />
    </span>
  );
}

export function AssistantPicker({ value, onChange, lang }: { value: AssistantChoice; onChange: (v: AssistantChoice) => void; lang: "es" | "en" }) {
  const es = lang === "es";
  const list = ASSISTANTS.filter((x) => x.gender === value.gender);
  const picked = getAssistant(value.id);
  const none = value.id === NO_ASSISTANT;
  const tabs = (
      <div className="mx-auto grid max-w-md grid-cols-3 rounded-2xl border border-border bg-card p-1 shadow-sm">
        {(["female", "male"] as const).map((g) => (
          <button key={g} type="button" onClick={() => onChange({ ...value, gender: g, id: !none && value.gender === g ? value.id : null })}
            className={cn("min-h-11 rounded-xl font-body text-sm font-semibold transition-colors", !none && value.gender === g ? "bg-primary text-foreground" : "text-muted-foreground hover:text-foreground")}>
            {g === "female" ? (es ? "Mujer" : "Woman") : (es ? "Hombre" : "Man")}
          </button>
        ))}
        <button type="button" onClick={() => onChange({ ...value, id: NO_ASSISTANT })} aria-pressed={none}
          className={cn("min-h-11 rounded-xl font-body text-sm font-semibold transition-colors", none ? "bg-primary text-foreground" : "text-muted-foreground hover:text-foreground")}>
          {es ? "Sin dibujo" : "No drawing"}
        </button>
      </div>
  );
  if (none) return (
    <div className="space-y-5">
      {tabs}
      <p className="mx-auto max-w-sm rounded-2xl border border-border bg-card p-4 text-center font-body text-sm text-muted-foreground">
        {es ? "Sin personaje: el panel se verá limpio, sin dibujos. Puedes activarlo cuando quieras desde Mi Perfil." : "No character: the panel stays clean, without drawings. You can turn it on anytime from My Profile."}
      </p>
      <div className="mx-auto max-w-sm space-y-1.5">
        <label className="font-body text-sm font-medium text-foreground" htmlFor="assistant-name-none">{es ? "¿Cómo quieres llamarle?" : "What would you like to call them?"}</label>
        <Input id="assistant-name-none" maxLength={30} value={value.name} placeholder="Sara" onChange={(e) => onChange({ ...value, name: e.target.value })} />
        <p className="font-body text-xs text-muted-foreground">{es ? "Si lo dejas vacío, se llamará Sara." : "Leave it empty and it will be called Sara."}</p>
      </div>
    </div>
  );
  return (
    <div className="space-y-5">
      {tabs}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {list.map((p) => {
          const sel = value.id === p.id;
          return (
            <button key={p.id} type="button" onClick={() => onChange({ ...value, id: p.id })} aria-pressed={sel}
              className={cn("group relative flex flex-col items-center rounded-2xl border bg-card px-2 pb-3 pt-3 text-center shadow-[0_4px_14px_hsl(var(--foreground)/0.08)] transition-all hover:-translate-y-0.5",
                sel ? "border-foreground ring-2 ring-primary" : "border-border")}>
              {sel && <span className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-background"><Check className="h-3.5 w-3.5" /></span>}
              <div className="relative flex h-36 w-full items-end justify-center sm:h-44">
                <span aria-hidden="true" className="absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/50 transition-transform group-hover:scale-105 sm:h-32 sm:w-32" />
                <img src={p.img} alt={p.name} loading="lazy" className="relative h-full w-auto object-contain drop-shadow-[0_6px_8px_hsl(var(--foreground)/0.15)]" />
              </div>
              <p className="mt-2 font-display text-base font-bold text-foreground">{p.name}</p>
              <p className="font-body text-xs text-muted-foreground">{es ? p.es : p.en}</p>
            </button>
          );
        })}
      </div>
      <div className="mx-auto max-w-sm space-y-1.5">
        <label className="font-body text-sm font-medium text-foreground" htmlFor="assistant-name">{es ? "¿Cómo quieres llamarle?" : "What would you like to call them?"}</label>
        <Input id="assistant-name" maxLength={30} value={value.name} placeholder={picked?.name ?? (es ? "Opcional" : "Optional")} onChange={(e) => onChange({ ...value, name: e.target.value })} />
      </div>
    </div>
  );
}
